import { useState } from "react";
import { useTranslation } from "../../context/TranslationContext";
import { MonitorCog, ChevronDown, Languages, Eye, EyeOff } from "lucide-react";
import { useUserSettings } from "./hooks/useUserSettings";
import { Language } from "../../i18n";

export function DisplaySettings() {
  const { t, language, setLanguage } = useTranslation();
  const { currentUser, updateShowWerklijstenSetting, updateShowColleagueHolidayRequestsSetting } = useUserSettings();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleWerklijstenToggle = async () => {
    try {
      const newValue = !(currentUser?.settings?.showWerklijsten ?? true);
      await updateShowWerklijstenSetting(newValue);
    } catch (error) {
      console.error("Failed to update werklijsten display setting:", error);
    }
  };

  const handleColleagueHolidayRequestsToggle = async () => {
    try {
      const newValue = !(currentUser?.settings?.showColleagueHolidayRequests ?? false);
      await updateShowColleagueHolidayRequestsSetting(newValue);
    } catch (error) {
      console.error("Failed to update colleague holiday requests display setting:", error);
    }
  };

  const languages: { code: Language; name: string; nativeName: string }[] = [
    { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'fr', name: 'French', nativeName: 'Français' },
  ];

  return (
    <div data-tsx-id="display-settings" className="bg-white border border-zinc-300 rounded-md">
      {/* Header - Clickable to toggle */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 rounded-md transition-colors"
      >
        <div className="flex items-center">
          <MonitorCog className="w-4 h-4 mr-2" />
          {t('settings.display')}
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="px-4 pb-3 space-y-3 border-t border-zinc-200 pt-3">
          {/* Werklijsten Toggle */}
          <div className="flex items-center justify-between hover:bg-zinc-50 rounded transition-colors">
            <span className="text-sm text-zinc-700">{t('settings.showWerklijsten')}</span>
            <button
              onClick={handleWerklijstenToggle}
              className="p-1 rounded hover:bg-zinc-200 transition-colors"
              title={currentUser?.settings?.showWerklijsten ?? true ? t('common.hide') : t('common.show')}
            >
              {currentUser?.settings?.showWerklijsten ?? true ? (
                <Eye className="w-4 h-4 text-blue-600" />
              ) : (
                <EyeOff className="w-4 h-4 text-zinc-400" />
              )}
            </button>
          </div>

          {/* Colleague Holiday Requests Toggle */}
          <div className="flex items-center justify-between hover:bg-zinc-50 rounded transition-colors">
            <span className="text-sm text-zinc-700">{t('settings.showColleagueHolidayRequests')}</span>
            <button
              onClick={handleColleagueHolidayRequestsToggle}
              className="p-1 rounded hover:bg-zinc-200 transition-colors"
              title={currentUser?.settings?.showColleagueHolidayRequests ?? false ? t('common.hide') : t('common.show')}
            >
              {currentUser?.settings?.showColleagueHolidayRequests ?? false ? (
                <Eye className="w-4 h-4 text-blue-600" />
              ) : (
                <EyeOff className="w-4 h-4 text-zinc-400" />
              )}
            </button>
          </div>

          {/* Language Selection */}
          <div className="flex items-center justify-between hover:bg-zinc-50 rounded transition-colors">
            <span className="flex items-center text-sm text-zinc-700"><Languages className="w-4 h-4 mr-2" />{t('common.language')}</span>
            <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as Language)}
                className="rounded-md border-zinc-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm py-2"
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.nativeName} ({lang.name})
                  </option>
                ))}
              </select>
            </div>
        </div>
      )}
    </div>
  );
}